import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import {
  getServiceStatus,
  getAllServiceStatus,
  getAllEnvsServiceStatus,
  queryGauthInfo,
  queryGauthCode,
} from '../../../api/services';
import { DeployArea, IGameURLParams } from '../../../model';
import {
  IAPIServiceConfigItem,
  ServiceSupportType,
  IServiceStatus,
  ServiceBusiness,
  IServiceAllEnvs,
  IGauthServiceType,
} from '../../../model/api-modules';
import { IRootState } from '../../../store/reducers';
import { IframeComponent } from './iframe';
import { IframeJumpComponent } from './iframe/jump-iframe';
import { ServicesApplyComponent } from '../service-apply';
import { checkIsEditor, getGameEnvIframeURL } from '../lib';
import { fetchUserPermission } from '../../../store/modules/permission';
import { PermissionCheck } from '../../component/permission-check';
import { getLocalStorageStudioId } from '../../utils/localstorage';
import { ServicesApplyModal } from '../service-apply/services-apply-modal';
import { GameEnvType, IGameEnvModel } from '../../../model/api-modules/env';
import { messageWarning } from '../../../components/message';
import getFormatMessage from '../../../locales/get-format-message';
import { getConfig } from '../../../config';

export enum GauthStatus {
  NoPermissions = 'noPermissions', // 没权限
  HasPermissions = 'hasPermissions', // 有权限
  Expired = 'expired', // 权限过期
  ApplyFailed = 'applyFailed', // 申请失败
  Pending = 'pending', // 待审批
  Frozen = 'frozen', // 冻结
  Denied = 'denied', // 被拒绝
}

export const GameConsoleRouter = () => {
  const location = useLocation();
  const routeParams = useParams<IGameURLParams>();
  const serviceConfigList = useSelector(
    (state: IRootState) => state.games.allServiceConfig
  );
  const commonDispatch = useDispatch<any>();
  const envs = useSelector((state: IRootState) => state.games.envs);
  const [isOpenService, setIsOpenService] = useState<boolean | undefined>(
    undefined
  ); // 是否有开通的服务
  const [servicesStatus, setServicesStatus] = useState<IServiceStatus[] | null>(
    null
  ); // 服务申请状态
  const [allServicesStatus, setAllServicesStatus] = useState<
    Record<string, IServiceStatus[] | null>
  >({}); // 所有服务申请状态
  // 内嵌页面传来的服务
  const [messageData, setMessageData] = useState<any | null>(null);
  const [messageServicesStatus, setMessageServicesStatus] = useState<
    IServiceStatus[] | null
  >(null); // 服务申请状态
  const [messageAllServicesStatus, setMessageAllServicesStatus] = useState<
    Record<string, IServiceStatus[] | null>
  >({}); // 所有服务申请状态
  const [showApplyModal, setShowApplyModal] = useState<boolean>(false);
  const [pathname, setPathname] = useState<string>();
  const [serviceAllEnvStatus, setServiceAllEnvStatus] = useState<
    Record<string, IServiceAllEnvs[] | null>
  >({});
  const [gauthStatus, setGauthStatus] = useState<GauthStatus>(
    GauthStatus.NoPermissions
  );
  const [gauthRes, setGauthRes] = useState<Record<string, any>>({});
  const [gauthCode, setGauthCode] = useState<IGauthServiceType | null>(null);
  const studioId = getLocalStorageStudioId() || '';
  const showApplyRef = useRef(false);
  const allEnvList = useSelector((state: IRootState) => state.games.allEnvList);
  const allGameList = useSelector((state: IRootState) => state.games.games);
  const selectGame = allGameList.find(
    (item) => item.gameID === routeParams.gameId
  );
  const userInfo = useSelector((state: IRootState) => state.common.userInfo);
  // const [iframeURL, setIframeURL] = useState<string>('');
  const deployArea: DeployArea = getConfig()?.deployArea;
  const isSingapore = deployArea === DeployArea.singapore;

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (
        event.data?.name === 'syncService' &&
        event.data?.data &&
        !showApplyRef.current
      ) {
        const serviceName = event.data?.data?.service_name || '';
        const isEditor = checkIsEditor({
          serviceName,
          envID: routeParams.envId,
        });
        if (!isEditor) {
          messageWarning(getFormatMessage('no_rbac_permission_action'));
          return;
        }
        showApplyRef.current = true;
        messageChange(event.data.data);
        setMessageData(event.data.data);
      }
      console.log('Received from iframe:', event);
      if (event.data?.name === 'goTimeConfig') {
        window.open(
          `${window.location.origin}/console/game-info/${routeParams.gameId}/${routeParams.envId}?activeKey=4`,
          '_blank'
        );
      }
    };

    window.addEventListener('message', handleMessage, false);
    return () => window.removeEventListener('message', handleMessage, false);
  }, []);

  useEffect(() => {
    init();
    const serviceItem = serviceConfigList?.find(
      (item) => item.id.toString() === routeParams.serviceId
    );
    if (serviceItem?.Business === ServiceBusiness.DMP) {
      getAllEnvStatus();
    }
  }, [location.pathname]);

  useEffect(() => {
    const serviceConfig = getSelectedServiceConfig(routeParams.serviceId);
    // 获取gauth权限
    if (serviceConfig?.Name === 'Store' && !isSingapore) {
      getAuthInfo();
    }
  }, [routeParams.serviceId]);

  const getAuthInfo = async () => {
    const result = await queryGauthCode(
      routeParams.gameId,
      routeParams.serviceId
    );
    setGauthCode(result);
    if (result?.gauthServiceType) {
      const [res1, res2] = await Promise.all([
        queryGauthInfo(36, result.gauthServiceType, routeParams.gameId),
        queryGauthInfo(20, result.gauthServiceType, routeParams.gameId),
      ]);
      //       正值表示鉴权成功，data段返回具体权限状态

      // 负值表示鉴权异常，可能是无权限记录、过期、冻结等：

      // -1 请求参数无效

      // -2 无申请记录

      // -3 申请的权限已经过期，请重新续期

      // -4 申请的权限已经被冻结

      // -5 申请的权限待审批中

      // -6 申请的权限被拒，请修改申请单

      // -7 申请的权限已被撤回，请修改申请单
      // data.iStatus  权限状态，-1 已拒绝 1 已通过 2 待审批 3 已过期 4 已冻结
      if (res1 && res2) {
        setGauthRes({
          res1,
          res2,
        });
        // 定义错误码与状态的映射配置
        const statusMap: Array<{
          check: (r: any) => boolean;
          status: GauthStatus;
        }> = [
          {
            check: (r: any) => r?.ret === 1 && r?.data?.iStatus === '1',
            status: GauthStatus.HasPermissions,
          },
          {
            check: (r: any) => r?.ret === -7,
            status: GauthStatus.ApplyFailed,
          },
          {
            check: (r: any) => r?.ret === -4,
            status: GauthStatus.Frozen,
          },
          {
            check: (r: any) => r?.ret === -6,
            status: GauthStatus.Denied,
          },
          {
            check: (r: any) => r?.ret === -3,
            status: GauthStatus.Expired,
          },
          {
            check: (r: any) => r?.ret === -1 || r?.ret === -2,
            status: GauthStatus.NoPermissions,
          },
          {
            check: (r: any) => r?.ret === -5,
            status: GauthStatus.Pending,
          },
        ];

        // 检查res1和res2，优先匹配有明确状态的规则
        let finalStatus = GauthStatus.NoPermissions;

        const allRes = [res1, res2];
        for (const res of allRes) {
          for (const mapping of statusMap) {
            if (mapping.check(res)) {
              finalStatus = mapping.status;
              break;
            }
          }
          if (finalStatus !== GauthStatus.NoPermissions) {
            break;
          }
        }

        // 如果没有匹配到特定状态，默认为NoPermissions
        setGauthStatus(finalStatus);
      } else {
        setGauthStatus(GauthStatus.NoPermissions);
      }
    } else {
      setGauthStatus(GauthStatus.NoPermissions);
    }
  };

  const getAllEnvStatus = async () => {
    const res = await getAllEnvsServiceStatus(
      routeParams.gameId,
      routeParams.serviceId
    );
    if (res) {
      setServiceAllEnvStatus(res);
    }
  };

  const init = async () => {
    setIsOpenService(undefined);
    setPathname(location.pathname);
    const perms = await checkPermission();
    if (!perms?.envPerm?.length) {
      return;
    }
    await queryServiceStatus(
      routeParams.gameId,
      routeParams.envId,
      routeParams.serviceId
    );

    // const env = getSelectedEnv(routeParams.envId);
    // const serviceConfig = getSelectedServiceConfig(routeParams.serviceId);
    // if (serviceConfig && env) {
    //   // open iframe
    //   const iframeUrl = await getGameEnvIframeURL(
    //     serviceConfig,
    //     env,
    //     routeParams.gameId,
    //     studioId,
    //     selectGame,
    //     userInfo
    //   ) || '';
    //   setIframeURL(iframeUrl);
    // }
  };

  const isDmpDevApplyDisabled = (
    env: IGameEnvModel,
    serviceConfig: IAPIServiceConfigItem
  ) => {
    if (env && serviceConfig) {
      if (serviceConfig.Business === ServiceBusiness.DMP) {
        const keyList = Object.keys(serviceAllEnvStatus) || [];
        // let devOpen = false;
        let liveOpen = false;
        const openList: string[] = [];
        keyList.forEach((keyItem) => {
          const envStatusItem = serviceAllEnvStatus[keyItem];
          if (envStatusItem && envStatusItem.length !== 0) {
            const closeItem = envStatusItem.find(
              (statusItem) =>
                statusItem.stage === 'opened' && statusItem.status === 2
            );
            if (!closeItem) {
              envStatusItem.forEach((statusItem) => {
                if (
                  statusItem.stage === 'opened' &&
                  statusItem.status === 1 &&
                  openList.indexOf(keyItem) === -1
                ) {
                  openList.push(keyItem);
                }
              });
            }
          }
        });
        openList.forEach((openItem) => {
          const envItem = allEnvList.find(
            (item) => item.envIncrementID === Number(openItem)
          );
          // if (envItem?.envType === GameEnvType.Dev) {
          //   devOpen = true;
          // }
          if (envItem?.envType === GameEnvType.Live) {
            liveOpen = true;
          }
        });
        return liveOpen && env.Type === GameEnvType.Dev;
      }
    }
    return false;
  };

  const messageChange = async (data: any) => {
    if (!data) {
      return;
    }
    const serviceItem = serviceConfigList?.find(
      (item) => item.Name === data?.service_name
    );
    if (!serviceItem) {
      return;
    }
    const res = await getServiceStatus(
      routeParams.gameId,
      Number(serviceItem.id),
      Number(routeParams.envId)
    );
    if (res) {
      setMessageServicesStatus(res);
    }
    const resAll = await getAllServiceStatus(
      routeParams.gameId,
      Number(routeParams.envId)
    );
    if (resAll) {
      setMessageAllServicesStatus(resAll);
    }
    setShowApplyModal(true);
  };

  const checkPermission = async () => {
    if (!routeParams.gameId) {
      return;
    }
    return await fetchUserPermission(commonDispatch)({
      gameID: routeParams.gameId,
      envID: routeParams.envId ? Number(routeParams.envId) : undefined,
    });
  };

  const queryServiceStatus = async (
    gameID: string,
    envID: string,
    serviceID: string
  ) => {
    const res = await getServiceStatus(
      gameID,
      Number(serviceID),
      Number(envID)
    );
    const resAll = await getAllServiceStatus(gameID, Number(envID));
    setServicesStatus(res);
    let currentServiceStatus = false;
    let isClose = false;
    if (res && res.length !== 0) {
      res.forEach((item) => {
        if (item.stage === 'opened') {
          currentServiceStatus = true;
        }
        if (item.stage === 'opened' && item.status === 2) {
          isClose = true;
        }
      });
      const openService = res.find(
        (it) => it.stage === 'opened' && it.status === 1
      );
      if (openService) {
        isClose = false;
      }
    }
    if (resAll) {
      setAllServicesStatus(resAll);
    }
    setIsOpenService(!isClose && currentServiceStatus);
  };

  const getSelectedServiceConfig = (serviceID: string) => {
    return serviceConfigList?.find((item) => item.id === Number(serviceID));
  };

  const getSelectedEnv = (envID: string) => {
    return envs.find((item) => item.id === Number(envID));
  };

  const renderIframeComponent = (
    serviceConfig: IAPIServiceConfigItem,
    url: string
  ) => {
    if (serviceConfig.SupportType === ServiceSupportType.Iframe) {
      return (
        <IframeComponent selectedServiceConfig={serviceConfig} url={url} />
      );
    }
    return (
      <IframeJumpComponent selectedServiceConfig={serviceConfig} url={url} />
    );
  };

  const onOk = () => {
    queryServiceStatus(
      routeParams.gameId,
      routeParams.envId,
      routeParams.serviceId
    );
  };

  const onApplyModalClose = () => {
    showApplyRef.current = false;
    setShowApplyModal(false);
  };

  const renderApplyModal = () => {
    const serviceItem = serviceConfigList?.find(
      (item) => item.Name === messageData?.service_name
    );
    if (!serviceItem) {
      return;
    }
    const env = getSelectedEnv(routeParams.envId);
    const serviceConfig = getSelectedServiceConfig(serviceItem.id.toString());
    if (!serviceConfig || !env) {
      return null;
    }
    if (showApplyModal && env && serviceConfig) {
      return (
        <ServicesApplyModal
          selectedGameID={routeParams.gameId}
          selectedEnv={env}
          selectedServiceConfig={serviceConfig}
          onClose={onApplyModalClose}
          onOk={onOk}
          selectedServerStatus={messageServicesStatus}
          allServicesStatus={messageAllServicesStatus}
          serviceAllEnvStatus={serviceAllEnvStatus}
        />
      );
    }
    return null;
  };

  const gauthCheck = () => {
    const serviceConfig = getSelectedServiceConfig(routeParams.serviceId);
    // 没有gauth权限
    if (
      serviceConfig?.Name === 'Store' &&
      gauthStatus !== GauthStatus.HasPermissions &&
      !isSingapore
    ) {
      return false;
    }
    return true;
  };

  const renderContent = () => {
    // the state value must be the current route
    if (location.pathname !== pathname) {
      return null;
    }

    if (isOpenService === undefined) {
      return null;
    }

    const env = getSelectedEnv(routeParams.envId);
    const serviceConfig = getSelectedServiceConfig(routeParams.serviceId);
    if (!serviceConfig || !env) {
      return null;
    }

    if (isDmpDevApplyDisabled(env, serviceConfig)) {
      return (
        <ServicesApplyComponent
          selectedGameID={routeParams.gameId}
          selectedServerStatus={servicesStatus}
          selectedServiceConfig={serviceConfig}
          selectedEnv={env}
          queryServiceStatus={queryServiceStatus}
          allServicesStatus={allServicesStatus}
        />
      );
    }

    // 1.14增加版本号注释该iframeURL，使用state iframeURL
    // open iframe
    const iframeURL = getGameEnvIframeURL(
      serviceConfig,
      env,
      routeParams.gameId,
      studioId,
      selectGame,
      userInfo
    );
    if (isOpenService && iframeURL && studioId && gauthCheck()) {
      return renderIframeComponent(serviceConfig, iframeURL);
    }

    // show apply page
    return (
      <ServicesApplyComponent
        selectedGameID={routeParams.gameId}
        selectedServerStatus={servicesStatus}
        selectedServiceConfig={serviceConfig}
        selectedEnv={env}
        queryServiceStatus={queryServiceStatus}
        allServicesStatus={allServicesStatus}
        gauthStatus={gauthStatus}
        gauthRes={gauthRes}
        gauthCode={gauthCode}
        getAuthInfo={getAuthInfo}
      />
    );
  };

  return (
    <PermissionCheck
      gameID={routeParams.gameId}
      envID={routeParams.envId}
      contentType="page"
    >
      <>
        {renderContent()}
        {renderApplyModal()}
      </>
    </PermissionCheck>
  );
};
